import React, { useState, useEffect } from 'react';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, Chart } from 'chart.js';

ChartJS.register(ArcElement, Tooltip, Legend);

// External label plugin with collision detection to prevent overlap
const externalLabelPlugin = {
  id: 'externalLabel',
  afterDraw(chart: Chart, _args: any, opts: any) {
    if (!opts?.enabled) return;
    const { ctx, data } = chart as any;
    const meta = chart.getDatasetMeta(0);
    if (!meta?.data) return;
    const raw: number[] = (data.datasets[0].data as number[]) || [];
    const total = raw.reduce((a, b) => a + b, 0) || 1;
    const styles = getComputedStyle(document.documentElement);
    const isDark = (styles.getPropertyValue('--theme-name') || 'dark').trim() === 'dark';
    const textColor = styles.getPropertyValue('--color-text').trim() || (isDark ? '#f0f3f6' : '#1e293b');
    const lineColor = styles.getPropertyValue('--color-border-soft').trim() || (isDark ? '#5f6b78' : '#64748b');
    const surfaceAlt = styles.getPropertyValue('--color-surface-alt').trim() || (isDark ? '#202733' : '#ffffff');
    const canvasH = (chart as any).height;
    const canvasW = (chart as any).width;
    const padX = 7, padY = 4;
    const boxH = 16 + padY;
    const minGap = 6; // minimum vertical gap between labels

    // Build label entries with natural positions
    interface LabelEntry {
      i: number; angle: number; sx: number; sy: number;
      naturalY: number; finalY: number; right: boolean;
      text: string; boxW: number; hx: number; ex: number;
    }
    const entries: LabelEntry[] = [];

    ctx.save();
    ctx.font = '600 11px system-ui,-apple-system,"Segoe UI",Roboto,sans-serif';

    meta.data.forEach((elem: any, i: number) => {
      if (!elem) return;
      const pctValue = (raw[i] / total) * 100;
      // Skip labels for very small slices (< 1.5%) to reduce clutter
      if (pctValue < 1.5) return;

      const angle = (elem.startAngle + elem.endAngle) / 2;
      const r = elem.outerRadius;
      const cx = elem.x;
      const cy = elem.y;
      const sx = cx + Math.cos(angle) * r * 0.95;
      const sy = cy + Math.sin(angle) * r * 0.95;
      const ex = cx + Math.cos(angle) * (r + 20);
      const ey = cy + Math.sin(angle) * (r + 20);
      const right = Math.cos(angle) >= 0;
      const horizontal = 24;
      const hx = ex + (right ? horizontal : -horizontal);

      const label = data.labels?.[i] ?? '';
      const value = raw[i];
      const pct = pctValue.toFixed(1) + '%';
      const text = opts.mode === 'both' ? `${label} ${pct} (${value.toLocaleString()})` : `${label} ${pct}`;
      const metrics = ctx.measureText(text);
      const boxW = metrics.width + padX * 2;

      entries.push({ i, angle, sx, sy, naturalY: ey, finalY: ey, right, text, boxW, hx, ex });
    });

    // Resolve vertical overlaps separately for left and right sides
    for (const side of [true, false]) {
      const sideEntries = entries.filter(e => e.right === side);
      // Sort by natural Y position
      sideEntries.sort((a, b) => a.naturalY - b.naturalY);

      // Push overlapping labels apart iteratively
      for (let pass = 0; pass < 16; pass++) {
        let moved = false;
        for (let j = 1; j < sideEntries.length; j++) {
          const prev = sideEntries[j - 1];
          const curr = sideEntries[j];
          const needed = boxH + minGap;
          const overlap = (prev.finalY + needed / 2) - (curr.finalY - needed / 2);
          if (overlap > 0) {
            const shift = overlap / 2 + 1; // +1 ensures progress each pass
            prev.finalY -= shift;
            curr.finalY += shift;
            moved = true;
          }
        }
        if (!moved) break;
      }

      // Clamp to canvas bounds
      for (const e of sideEntries) {
        const boxY = e.finalY - boxH / 2;
        if (boxY < 4) e.finalY = 4 + boxH / 2;
        if (boxY + boxH > canvasH - 4) e.finalY = canvasH - 4 - boxH / 2;
      }
    }

    // Draw all labels
    for (const e of entries) {
      const ey = e.finalY;
      const boxX = e.right ? e.hx : e.hx - e.boxW;
      const boxY = ey - boxH / 2;

      // Clamp horizontal to canvas
      const clampedBoxX = Math.max(2, Math.min(boxX, canvasW - e.boxW - 2));

      // Connector line: start at arc edge, elbow to label
      ctx.lineWidth = 1.2;
      ctx.strokeStyle = lineColor;
      ctx.beginPath();
      ctx.moveTo(e.sx, e.sy);
      ctx.lineTo(e.ex, e.naturalY);
      ctx.lineTo(e.right ? clampedBoxX : clampedBoxX + e.boxW, ey);
      ctx.stroke();

      // Label background pill
      const rad = 7;
      ctx.fillStyle = isDark ? surfaceAlt + 'ea' : surfaceAlt + 'f4';
      ctx.beginPath();
      ctx.moveTo(clampedBoxX + rad, boxY);
      ctx.lineTo(clampedBoxX + e.boxW - rad, boxY);
      ctx.quadraticCurveTo(clampedBoxX + e.boxW, boxY, clampedBoxX + e.boxW, boxY + rad);
      ctx.lineTo(clampedBoxX + e.boxW, boxY + boxH - rad);
      ctx.quadraticCurveTo(clampedBoxX + e.boxW, boxY + boxH, clampedBoxX + e.boxW - rad, boxY + boxH);
      ctx.lineTo(clampedBoxX + rad, boxY + boxH);
      ctx.quadraticCurveTo(clampedBoxX, boxY + boxH, clampedBoxX, boxY + boxH - rad);
      ctx.lineTo(clampedBoxX, boxY + rad);
      ctx.quadraticCurveTo(clampedBoxX, boxY, clampedBoxX + rad, boxY);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = lineColor;
      ctx.stroke();

      // Label text
      ctx.fillStyle = textColor;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.shadowColor = isDark ? 'rgba(0,0,0,.55)' : 'rgba(255,255,255,.35)';
      ctx.shadowBlur = 2;
      ctx.fillText(e.text, clampedBoxX + padX, ey);
    }
    ctx.restore();
  }
};

if (!(ChartJS as any).registry.plugins.get('externalLabel')) { ChartJS.register(externalLabelPlugin as any); }

export interface PieDatum { name: string; value: number; }
interface Props { title?: string; data: PieDatum[]; height?: number; donut?: boolean; externalLabels?: boolean; hideHeader?: boolean; }

const COLORS = ['#3d8bfd','#9353d3','#ff6e6e','#61dafb','#3fb950','#e3b341','#f85149','#2563eb','#0ea5e9','#7c3aed','#db2777','#059669','#d97706','#dc2626'];

export const PieChartCard: React.FC<Props> = ({ title, data, height=300, donut=false, externalLabels=true, hideHeader=false }) => {
  const [showValues, setShowValues] = useState(false);
  const [useExternal, setUseExternal] = useState(externalLabels);
  const [mobile, setMobile] = useState(false);
  // Responsive flag + toggle external labels only on non-mobile
  useEffect(()=>{ const resize=()=>{ const narrow=window.matchMedia('(max-width:680px)').matches; setMobile(narrow); setUseExternal(externalLabels && !narrow); }; resize(); window.addEventListener('resize', resize); return ()=>window.removeEventListener('resize', resize); }, [externalLabels]);

  const chartData = { labels: data.map(d=>d.name), datasets:[{ data: data.map(d=>d.value), backgroundColor: data.map((_,i)=>COLORS[i%COLORS.length]), borderColor:'var(--color-border,#222)', borderWidth:1, hoverOffset:6 }] };

  const legendPosition = mobile ? 'bottom' : 'right';

  // Layout padding: keep external label padding generous, tighten when internal
  const options:any = { maintainAspectRatio:false, responsive:true,
    layout: useExternal ? { padding:{ left:32,right:32,top: hideHeader?30:24,bottom: mobile?18:56 } } : { padding:{ left:8,right:8,top: hideHeader?10:8,bottom: mobile?8:16 } },
    plugins:{ legend:{ display:!useExternal, position:legendPosition, align:'center', labels:{ boxWidth:14, padding:8, color:getComputedStyle(document.documentElement).getPropertyValue('--color-text').trim()||'#f0f3f6', font:{ size: mobile?10:11, weight:500 } } }, tooltip:{ callbacks:{ label:(ctx:any)=>{ const v=ctx.raw; const total=ctx.dataset.data.reduce((a:number,b:number)=>a+b,0)||1; const pct=((v/total)*100).toFixed(1)+'%'; return showValues?`${ctx.label}: ${v.toLocaleString()} (${pct})`:`${ctx.label}: ${pct}`; } } }, externalLabel:{ enabled:useExternal, mode: showValues?'both':'percent' } }, cutout: donut?'55%':undefined, animation:{ duration:450 } };

  // Heights: On mobile allow auto outer height and fix inner chart (including legend) to a compact constant to avoid overflow & overlap.
  const MOBILE_CHART_TOTAL = 250; // includes legend inside canvas
  const headerSpace = hideHeader ? 0 : 34; // rough header height for desktop subtraction logic
  const desktopInnerHeight = height - headerSpace; // legend & external labels handled via Chart.js layout

  return (
    <div className="chart-surface stack-sm" style={{ height: mobile? 'auto': height, paddingBottom: useExternal? '1.0rem': undefined, position:'relative', overflow:'hidden' }}>
      {!hideHeader && (
        <div style={{ display:'flex', alignItems:'center', justifyContent:'center', position:'relative' }}>
          {title && <h5 className="mb-2 text-center" style={{ flex:'1 1 auto', fontSize: mobile? '.9rem': undefined }}>{title}</h5>}
          <button type="button" aria-label={showValues? 'Show percentages only':'Show values and percentages'} onClick={()=>setShowValues(v=>!v)} className="btn btn-sm" style={{ position:'absolute', top: mobile? -2 : -4, right: mobile? -2 : -4, fontSize:'0.65rem', background:'var(--color-surface-alt)', border:'1px solid var(--color-border-soft)', color:'var(--color-text)', padding:'.25rem .45rem', borderRadius:6 }}>{showValues?'%':'123'}</button>
        </div>
      )}
      {hideHeader && (
        <button type="button" aria-label={showValues? 'Show percentages only':'Show values and percentages'} onClick={()=>setShowValues(v=>!v)} className="btn btn-sm" style={{ position:'absolute', top:8, right:12, fontSize:'0.65rem', background:'var(--color-surface-alt)', border:'1px solid var(--color-border-soft)', color:'var(--color-text)', padding:'.25rem .45rem', borderRadius:6, zIndex:5 }}>{showValues?'%':'123'}</button>
      )}
      <div style={{ position:'relative', width:'100%', height: mobile? MOBILE_CHART_TOTAL : desktopInnerHeight, maxWidth:'100%' }} aria-label={title}>
        <Pie data={chartData} options={options} />
      </div>
    </div>
  );
};

export default PieChartCard;
